/**
 * "Reset Google Authenticator" for someone else: the lost or replaced
 * phone. It lowers their account to one factor until they set up again, so
 * it asks once, in place, before doing it. The backend decides who may
 * (sysadmin/mfaReset.ts); this is shown only where it will say yes.
 */
import { useState } from 'react';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { call, isCallFailure } from '../../lib/callable';
import page from '../../shared/Page.module.css';

interface ResetResult { success: boolean; hadFactor: boolean; notified: boolean; message: string }

export function ResetMfaButton({ targetUid, orgId, who, onDone, invalidate = [], buttonClassName }: {
  targetUid: string;
  /** The organization whose System Admin this is; omitted for a platform system administrator. */
  orgId?: string;
  /** Who it is for, in the accessible name and the question: an email or a name. */
  who: string;
  onDone?: (message: string) => void;
  invalidate?: QueryKey[];
  buttonClassName?: string;
}) {
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const reset = useMutation({
    mutationFn: () => call<{ targetUid: string; orgId?: string }, ResetResult>('sysAdminResetMfa', { targetUid, orgId }),
    onSuccess: (r) => {
      setAsking(false);
      onDone?.(r.message);
      for (const key of invalidate) void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  const btn = buttonClassName ?? page.btn;

  if (!asking) {
    return (
      <button type="button" className={btn} aria-label={`Reset Google Authenticator for ${who}`} onClick={() => { reset.reset(); setAsking(true); }}>
        Reset Google Authenticator
      </button>
    );
  }
  return (
    <span role="group" aria-label={`Confirm the reset for ${who}`} style={{ display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 'var(--fs-meta)', color: 'var(--ink-2)' }}>Signs them out and asks them to set up again.</span>
      <button type="button" className={btn} style={{ borderColor: 'var(--danger-ink)', color: 'var(--danger-ink)' }} disabled={reset.isPending} onClick={() => reset.mutate()}>
        {reset.isPending ? 'Resetting…' : 'Reset'}
      </button>
      <button type="button" className={btn} disabled={reset.isPending} onClick={() => setAsking(false)}>Cancel</button>
      {reset.error && <span role="alert" style={{ color: 'var(--danger-ink)', fontSize: 'var(--fs-meta)' }}>{isCallFailure(reset.error) ? reset.error.message : 'The reset did not go through.'}</span>}
    </span>
  );
}
